import { useEffect, useState } from 'react';

const DEFAULT_DEBOUNCE_DELAY = 500;

/**
 * A hook that debounces a value. The debounced value will only update once the input value has remained the same for the specified delay.
 *
 * @template Value - The type of the value to debounce.
 * @param {Value} value - The value to debounce.
 * @param {number} delayInMs - The delay in milliseconds to wait before updating the debounced value.
 * @returns {Value} - The debounced value.
 */
export function useDebounce<Value>(value: Value, delayInMs: number = DEFAULT_DEBOUNCE_DELAY): Value {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delayInMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, delayInMs]);

  return debouncedValue;
}
